import { DOCUMENT } from '@angular/common';
import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';

import { filter, switchMap } from 'rxjs';

import { TuiRoot } from '@taiga-ui/core';

import { AuthenticationFacadeService } from '@finverity/authentication';
import { FinLayoutService } from '@finverity/platform-app/core/layout/services';
import { FinApiErrorHandler, FinEnvironmentService } from '@finverity/platform-app/core/services';
import { SelectedInstrumentsSummaryPopupHostComponent } from '@finverity/platform-app/modules/instrument/features/selected-instruments-summary';
import { BrandOverrideService, FvConfirmService, FvSnackbarService } from '@fv/ui-kit';

/** A browser-tab icon `<link>` from `index.html`, with the attributes it shipped with. */
interface ShippedIconLink {
  readonly element: HTMLLinkElement;
  readonly href: string | null;
  readonly type: string | null;
}

/**
 * Puts an attribute back to its shipped value, or removes it when the element shipped without it.
 *
 * @param element - Element to update.
 * @param name - Attribute name.
 * @param shippedValue - Value the element shipped with, or `null` when the attribute was absent.
 */
function restoreAttribute(element: Element, name: string, shippedValue: string | null): void {
  if (shippedValue === null) {
    element.removeAttribute(name);
  } else {
    element.setAttribute(name, shippedValue);
  }
}

@Component({
  selector: 'fin-platform-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, TuiRoot, SelectedInstrumentsSummaryPopupHostComponent],
})
export class AppComponent implements OnInit {
  private readonly titleService = inject(Title);
  private readonly environmentService = inject(FinEnvironmentService);
  private readonly authFacade = inject(AuthenticationFacadeService);
  private readonly layoutService = inject(FinLayoutService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(FvSnackbarService);
  private readonly confirmDialogService = inject(FvConfirmService);
  private readonly router = inject(Router);
  private readonly brandOverride = inject(BrandOverrideService);
  private readonly document = inject(DOCUMENT);

  readonly favIcon: HTMLLinkElement | null = document.querySelector('#appIcon');

  constructor() {
    this.initializeErrorHandler();
    if (this.brandOverride.enabled) {
      this.followBrandOverrideTitleAndFavicon();
    }
  }

  ngOnInit() {
    this.setTitleAndFavicon();
    this.handleAuthLayoutBackground();
    this.handlePasswordExpirationNotification();
  }

  private initializeErrorHandler(): void {
    // TODO: performance_issue - Stopping application to initialize faster
    FinApiErrorHandler.init();
  }

  private setTitleAndFavicon(): void {
    this.titleService.setTitle(this.environmentService.platform.title);
    if (this.favIcon) {
      this.favIcon.href = this.environmentService.platform.faviconUrl;
    }
  }

  /**
   * Keeps the title on the brand-preview app name and every browser-tab icon on the brand-preview
   * favicon while they are set; otherwise the environment title and the icons shipped in `index.html`.
   * Must run in the injection context.
   */
  private followBrandOverrideTitleAndFavicon(): void {
    const shippedIconLinks: ShippedIconLink[] = Array.from(
      this.document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'),
      element => ({ element, href: element.getAttribute('href'), type: element.getAttribute('type') })
    );
    effect(() => {
      this.titleService.setTitle(this.brandOverride.appName() ?? this.environmentService.platform.title);
      const previewFavicon = this.brandOverride.favicon();
      for (const iconLink of shippedIconLinks) {
        if (previewFavicon) {
          iconLink.element.setAttribute('href', previewFavicon);
          iconLink.element.removeAttribute('type');
        } else {
          restoreAttribute(iconLink.element, 'href', iconLink.href);
          restoreAttribute(iconLink.element, 'type', iconLink.type);
        }
      }
    });
  }

  private handleAuthLayoutBackground(): void {
    this.authFacade.loggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loggedIn => {
      if (loggedIn) {
        this.layoutService.setAuthLayoutBackgroundColor();
      } else {
        this.layoutService.removeAuthLayoutBackgroundColor();
      }
    });
  }

  private handlePasswordExpirationNotification(): void {
    this.authFacade.loggedIn$
      .pipe(
        filter(loggedIn => loggedIn),
        filter(() => {
          const user = this.authFacade.userSignal();
          return !user?.is_password_never_expiring && !user?.is_service_account;
        }),
        switchMap(() =>
          this.authFacade.isInGracePeriod$.pipe(
            filter(isInGracePeriod => !!isInGracePeriod),
            switchMap(() => this.authFacade.daysUntilExpiration$),
            filter(daysUntilExpiration => daysUntilExpiration !== null && daysUntilExpiration !== undefined)
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(daysUntilExpiration => {
        this.snackbarService
          .warning(
            `Your password expires in ${daysUntilExpiration} days. Click [Change Password] to update it now.`,
            'Password Expiration'
          )
          .subscribe();

        this.confirmDialogService
          .warning({
            title: 'Password Expiration Warning',
            content: `Your password will expire in ${daysUntilExpiration} days. Would you like to change it now?`,
            submitBtnText: 'Change Password',
            cancelBtnText: 'Later',
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(confirmed => {
            if (confirmed) {
              this.router
                .navigate(['/user-profile/details'], {
                  state: {
                    activeTab: 1,
                    fromPasswordExpiration: true,
                  },
                })
                .then();
            }
          });
      });
  }
}
